import { format, addDays, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Settings2, RotateCcw, Save, CalendarIcon, Clock,
  ChevronLeft, ChevronRight, Loader2, Plus, Unlock, Lock,
  Sparkles, MoreVertical, Maximize2, Anchor,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface FloorPlanControlBarProps {
  selectedDate: Date;
  onDateChange?: (date: Date) => void;
  selectedTimeSlot: string | null;
  onTimeSlotChange: (slot: string) => void;
  timeSlots: { start: string; end: string }[];
  isCurrentSlotActive: boolean;
  onGoToNow: () => void;
  timeSlotReservationCount: number;
  isEditMode: boolean;
  onToggleEditMode: () => void;
  freeMovementMode: boolean;
  onToggleFreeMovement: () => void;
  hasUnsavedChanges: boolean;
  onSave: () => void;
  isSaving: boolean;
  onShowAddFixture: () => void;
  onShowReset: () => void;
  onShowFloorPlanRequest: () => void;
  hideAssistedSetup?: boolean;
  onExpand?: () => void;
  onAnchorView?: () => void;
  isAnchoring?: boolean;
}

export const FloorPlanControlBar = ({
  selectedDate,
  onDateChange,
  selectedTimeSlot,
  onTimeSlotChange,
  timeSlots,
  isCurrentSlotActive,
  onGoToNow,
  timeSlotReservationCount,
  isEditMode,
  onToggleEditMode,
  freeMovementMode,
  onToggleFreeMovement,
  hasUnsavedChanges,
  onSave,
  isSaving,
  onShowAddFixture,
  onShowReset,
  onShowFloorPlanRequest,
  hideAssistedSetup,
  onExpand,
  onAnchorView,
  isAnchoring = false,
}: FloorPlanControlBarProps) => {
  const currentTimeSlotIndex = timeSlots.findIndex(ts => ts.start === selectedTimeSlot);
  const isToday = formatDateLocal(selectedDate) === formatDateLocal(new Date());

  const handlePreviousDay = () => onDateChange?.(subDays(selectedDate, 1));
  const handleNextDay = () => onDateChange?.(addDays(selectedDate, 1));
  const handleToday = () => onDateChange?.(new Date());

  const handlePreviousTimeSlot = () => {
    if (currentTimeSlotIndex > 0) {
      onTimeSlotChange(timeSlots[currentTimeSlotIndex - 1].start);
    }
  };

  const handleNextTimeSlot = () => {
    if (currentTimeSlotIndex < timeSlots.length - 1) {
      onTimeSlotChange(timeSlots[currentTimeSlotIndex + 1].start);
    }
  };

  return (
    <>
    <Card className="p-4">
      <div className="flex flex-col gap-4">
        {/* Navigation row */}
        <div className="flex flex-col gap-3">
          {/* Date navigation */}
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePreviousDay}
              title="Jour précédent"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[140px] sm:w-[160px] justify-center text-center font-medium",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 hidden sm:inline" />
                  {format(selectedDate, "EEE d MMM", { locale: fr })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && onDateChange?.(date)}
                  initialFocus
                  locale={fr}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleNextDay}
              title="Jour suivant"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToday}
              className="text-xs hidden sm:flex"
            >
              Aujourd'hui
            </Button>
          </div>

          {/* Time slot navigation */}
          <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePreviousTimeSlot}
                disabled={currentTimeSlotIndex <= 0}
                title="Créneau précédent"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className={cn(
                "flex items-center gap-1 min-w-[120px] sm:min-w-[140px] justify-center px-2 sm:px-3 py-1 rounded-md transition-colors text-sm",
                isCurrentSlotActive && "bg-green-100 dark:bg-green-900/30"
              )}>
                <Clock className={cn(
                  "h-4 w-4 hidden sm:inline",
                  isCurrentSlotActive ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
                )} />
                <span className="font-medium">
                  {selectedTimeSlot && timeSlots.find(ts => ts.start === selectedTimeSlot) 
                    ? `${selectedTimeSlot} - ${timeSlots.find(ts => ts.start === selectedTimeSlot)?.end}`
                    : 'Aucun'}
                </span>
                {isCurrentSlotActive && (
                  <span className="ml-1 text-xs text-green-600 dark:text-green-400 font-medium hidden sm:inline">
                    • En cours
                  </span>
                )}
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={handleNextTimeSlot}
                disabled={currentTimeSlotIndex >= timeSlots.length - 1}
                title="Créneau suivant"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            <span className="text-sm text-muted-foreground">
              {timeSlotReservationCount} résa.
            </span>
            
            {isToday && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onGoToNow}
                className="text-xs"
                title="Aller au créneau actuel"
              >
                Maintenant
              </Button>
            )}
          </div>
        </div>


        {/* Edit mode controls - stay inside card */}
        {isEditMode && (
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                {/* Desktop edit mode buttons */}
                <div className="hidden sm:flex items-center gap-2">
                  <Button
                    variant={freeMovementMode ? "default" : "outline"}
                    size="sm"
                    onClick={onToggleFreeMovement}
                    title={freeMovementMode ? "Le chevauchement est autorisé" : "Le chevauchement est bloqué"}
                    className={cn(
                      freeMovementMode && "bg-orange-500 hover:bg-orange-600 text-white"
                    )}
                  >
                    {freeMovementMode ? (
                      <Unlock className="w-4 h-4 mr-2" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    {freeMovementMode ? "Libre" : "Verrouillé"}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={onShowAddFixture}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un élément
                  </Button>
                  <Button variant="outline" size="sm" onClick={onShowReset}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Réinitialiser
                  </Button>
                </div>
                
                {/* Mobile edit mode dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="sm:hidden">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onToggleFreeMovement}>
                      {freeMovementMode ? (
                        <Unlock className="w-4 h-4 mr-2" />
                      ) : (
                        <Lock className="w-4 h-4 mr-2" />
                      )}
                      {freeMovementMode ? "Mode libre" : "Mode verrouillé"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onShowAddFixture}>
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un élément
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onShowReset}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {hasUnsavedChanges && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    <span className="hidden sm:inline">Sauvegarder</span>
                    <span className="sm:hidden">Sauver</span>
                  </Button>
                )}
          </div>
        )}
      </div>
    </Card>

    {/* Action buttons - outside card, closer to the canvas */}
    <div className="flex flex-wrap items-center justify-end gap-2 mt-2">
      {/* Assisted configuration button */}
      {!hideAssistedSetup && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onShowFloorPlanRequest}
                className="border-primary/50 text-primary hover:bg-primary/10 hover:text-primary gap-1 sm:gap-2"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Configuration assistée</span>
                <span className="sm:hidden">Aide</span>
                <span className="text-xs bg-primary/10 px-1.5 py-0.5 rounded-full font-medium hidden sm:inline">Gratuit</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[280px]">
              <p>Notre équipe configure votre plan de salle sous 12h ouvrées. Envoyez-nous simplement une photo ou un schéma de votre salle.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      {onAnchorView && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onAnchorView}
                disabled={isAnchoring}
                className="gap-2"
              >
                {isAnchoring ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Anchor className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Ancrer</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Sauvegarder cette position et ce zoom pour tous les appareils</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {onExpand && (
        <Button
          variant="outline"
          size="sm"
          onClick={onExpand}
          className="gap-2"
        >
          <Maximize2 className="w-4 h-4" />
          <span className="hidden sm:inline">Agrandir</span>
        </Button>
      )}
      
      <Button 
        variant={isEditMode ? "default" : "outline"} 
        size="sm"
        onClick={onToggleEditMode}
      >
        <Settings2 className="w-4 h-4 sm:mr-2" />
        <span className="hidden sm:inline">{isEditMode ? "Terminer" : "Modifier le plan"}</span>
      </Button>
    </div>
    </>
  );
};
